<template>
  <div class="mango-training-view pa-4">
    <!-- 1. Header / Tarjeta de Título Neutra (Estándar SunExpert) -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-3 px-5 flex-wrap">
        <v-icon color="primary" large class="mr-3">mdi-brain</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Entrenamiento y Etiquetado IA
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Módulo de calibración visual y asignación de madurez para el modelo de visión artificial.
          </span>
        </div>
        <v-spacer></v-spacer>

        <!-- Estado del Modelo IA y acción de Reentrenar -->
        <div class="d-flex align-center flex-wrap my-1">
          <v-chip
            :color="getTrainingStatusColor()"
            small
            outlined
            class="font-weight-bold mr-2"
          >
            <v-icon left x-small>
              {{ trainingStatus === 'TRAINING' ? 'mdi-loading mdi-spin' : 'mdi-check-circle-outline' }}
            </v-icon>
            {{ getTrainingStatusText() }}
          </v-chip>
          <v-chip v-if="modelVersion" small outlined color="grey darken-1" class="mr-3">
            v{{ modelVersion }}
          </v-chip>
          <v-btn
            v-if="hasRetrainPermission"
            color="primary"
            outlined
            small
            class="font-weight-bold"
            :disabled="trainingStatus === 'TRAINING'"
            @click="confirmRetrain"
          >
            <v-icon left small>
              {{ trainingStatus === 'TRAINING' ? 'mdi-refresh mdi-spin' : 'mdi-rotate-3d-variant' }}
            </v-icon>
            {{ trainingStatus === 'TRAINING' ? 'Entrenando...' : 'Reentrenar Modelo' }}
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- Input de archivo oculto con selección múltiple (hasta 30 imágenes) -->
    <input 
      type="file" 
      ref="fileInput" 
      accept="image/*" 
      multiple 
      style="display: none" 
      @change="onFilesSelected"
    />

    <!-- 2. ESTADO 0: VACÍO (Sin lote de imágenes cargado) -->
    <v-card 
      v-if="batchImages.length === 0" 
      class="rounded-xl border-light text-center py-12 px-6" 
      outlined 
      elevation="0"
    >
      <div class="mb-4">
        <v-avatar size="72" color="blue lighten-5">
          <v-icon size="40" color="primary">mdi-image-multiple-outline</v-icon>
        </v-avatar>
      </div>
      <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-4 mb-1">
        Carga de Imágenes para Entrenamiento
      </h3>
      <p class="text-caption grey--text text--darken-2 mb-5">
        Puedes seleccionar una o varias imágenes de mangos (máximo 30 por lote).
      </p>
      <v-btn 
        color="primary" 
        large 
        depressed 
        class="font-weight-bold px-6" 
        @click="$refs.fileInput.click()"
      >
        <v-icon left medium>mdi-folder-upload-outline</v-icon>
        Cargar Lote de Imágenes (Hasta 30)
      </v-btn>
      <div class="caption grey--text text--darken-1 mt-3">
        * Límite de 30 fotos por lote para asegurar la fluidez y rendimiento del sistema.
      </div>
    </v-card>

    <!-- 3. ESTADO 1: GALERÍA / COLLAGE DEL LOTE -->
    <div v-else-if="viewMode === 'gallery'">
      <!-- Barra de Control del Lote -->
      <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
        <v-card-text class="py-3 px-5">
          <v-row dense align="center" justify="space-between">
            <!-- Información y conteo del lote -->
            <v-col cols="12" md="4" class="d-flex align-center flex-wrap">
              <v-chip color="primary" small outlined class="font-weight-bold mr-2">
                {{ batchImages.length }} imágenes
              </v-chip>
              <v-chip color="success" small outlined class="font-weight-bold mr-2">
                {{ savedCount }} guardadas
              </v-chip>
              <v-chip v-if="pendingCount > 0" color="grey darken-1" small outlined class="font-weight-bold">
                {{ pendingCount }} pendientes
              </v-chip>
            </v-col>

            <!-- Asignación rápida de madurez a todo el lote -->
            <v-col cols="12" md="5" class="d-flex align-center justify-center justify-md-start flex-wrap my-2 my-md-0">
              <span class="caption grey--text text--darken-3 font-weight-bold mr-2">
                Madurez Lote:
              </span>
              <v-chip-group v-model="globalBatchMaturity" mandatory active-class="primary--text font-weight-bold">
                <v-chip
                  v-for="lvl in maturityLevels"
                  :key="lvl.value"
                  :value="lvl.value"
                  small
                  :color="globalBatchMaturity === lvl.value ? lvl.color : ''"
                  :dark="globalBatchMaturity === lvl.value"
                  class="mr-1"
                >
                  {{ lvl.value }}
                </v-chip>
              </v-chip-group>
              <v-btn
                x-small
                outlined
                color="primary"
                class="font-weight-bold ml-1"
                @click="applyGlobalMaturityToAll"
              >
                Aplicar a Todas
              </v-btn>
            </v-col>

            <!-- Botones de Acción -->
            <v-col cols="12" md="3" class="d-flex align-center justify-end flex-wrap">
              <v-btn 
                color="primary" 
                small 
                depressed 
                class="font-weight-bold mr-2" 
                :loading="isBatchProcessing"
                :disabled="pendingCount === 0"
                @click="processEntireBatch"
              >
                <v-icon left small>mdi-play-circle-outline</v-icon>
                Procesar Lote
              </v-btn>
              <v-btn 
                small 
                text 
                color="error" 
                class="font-weight-bold" 
                :disabled="isBatchProcessing"
                @click="clearBatch"
              >
                <v-icon left small>mdi-close</v-icon>
                Limpiar Lote
              </v-btn>
            </v-col>
          </v-row>

          <!-- Barra de Progreso de Procesamiento Masivo -->
          <div v-if="isBatchProcessing" class="mt-3">
            <div class="d-flex justify-space-between caption grey--text text--darken-3 mb-1">
              <span>{{ batchProgressText }}</span>
              <span class="font-weight-bold">{{ batchProgressPercent }}%</span>
            </div>
            <v-progress-linear
              :value="batchProgressPercent"
              color="primary"
              height="8"
              rounded
              striped
            ></v-progress-linear>
          </div>
        </v-card-text>
      </v-card>

      <!-- Cuadrícula / Collage de Tarjetas (8 fotos por página) -->
      <v-row dense>
        <v-col 
          v-for="(imgItem, idx) in paginatedBatch" 
          :key="imgItem.id" 
          cols="12" 
          sm="6" 
          md="4" 
          lg="3"
        >
          <v-card 
            class="rounded-xl border-light h-100 collage-card" 
            outlined 
            elevation="0"
            @click="openCanvas(imgItem.originalIndex)"
          >
            <!-- Previsualización de Imagen -->
            <div class="image-preview-wrapper">
              <v-img 
                :src="imgItem.url" 
                height="170" 
                contain 
                class="bg-grey-lighten-5"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>

              <!-- Chip de Estado Superior -->
              <div class="status-overlay">
                <v-chip 
                  v-if="imgItem.saved" 
                  small 
                  color="success" 
                  dark 
                  class="font-weight-bold elevation-1"
                >
                  <v-icon left x-small>mdi-check-circle</v-icon> Guardado
                </v-chip>
                <v-chip 
                  v-else-if="imgItem.boxes && imgItem.boxes.length > 0" 
                  small 
                  color="primary" 
                  outlined 
                  class="font-weight-bold bg-white elevation-1"
                >
                  {{ imgItem.boxes.length }} mangos
                </v-chip>
                <v-chip 
                  v-else 
                  small 
                  color="grey darken-1" 
                  outlined 
                  class="font-weight-bold bg-white elevation-1"
                >
                  Pendiente
                </v-chip>
              </div>
            </div>

            <!-- Información y Botón de Edición -->
            <v-card-text class="py-3 px-4">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 140px;">
                  Foto #{{ imgItem.originalIndex + 1 }}
                </span>
                <v-chip 
                  v-if="getMaturityBadge(imgItem)" 
                  x-small 
                  :color="getMaturityBadge(imgItem).color" 
                  :dark="getMaturityBadge(imgItem).dark"
                  class="font-weight-bold"
                >
                  {{ getMaturityBadge(imgItem).text }}
                </v-chip>
                <span v-else class="caption grey--text text--darken-1 font-italic">Sin madurez</span>
              </div>

              <div class="caption grey--text text--darken-2 text-truncate mb-2">
                {{ imgItem.file.name }}
              </div>

              <v-btn 
                small 
                outlined 
                color="primary" 
                block 
                class="font-weight-bold"
                @click.stop="openCanvas(imgItem.originalIndex)"
              >
                <v-icon left small>mdi-crop-free</v-icon>
                Editar en Lienzo
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Paginador del Collage -->
      <div v-if="totalPages > 1" class="text-center mt-5 mb-2">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          color="primary"
          circle
        ></v-pagination>
      </div>
    </div>

    <!-- 4. ESTADO 2: LIENZO DE ETIQUETADO DETALLADO -->
    <div v-else-if="viewMode === 'canvas'">
      <!-- Barra Superior de Navegación del Lienzo -->
      <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
        <v-card-title class="d-flex align-center justify-space-between py-2 px-5 flex-wrap">
          <div class="d-flex align-center">
            <v-btn 
              small 
              outlined 
              color="primary" 
              class="font-weight-bold mr-3" 
              @click="backToGallery"
            >
              <v-icon left small>mdi-arrow-left</v-icon>
              Volver a la Galería
            </v-btn>

            <div>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                Foto #{{ activeIndex + 1 }} de {{ batchImages.length }}
              </span>
              <span class="caption grey--text text--darken-2 ml-2">
                ({{ currentItem ? currentItem.file.name : '' }})
              </span>
            </div>
          </div>

          <!-- Controles de Navegación Anterior / Siguiente -->
          <div class="d-flex align-center my-1">
            <v-btn 
              small 
              text 
              color="grey darken-2" 
              :disabled="activeIndex === 0" 
              class="font-weight-bold mr-1" 
              @click="navigateImage(-1)"
            >
              <v-icon left small>mdi-chevron-left</v-icon> Anterior
            </v-btn>
            <v-btn 
              small 
              text 
              color="grey darken-2" 
              :disabled="activeIndex === batchImages.length - 1" 
              class="font-weight-bold" 
              @click="navigateImage(1)"
            >
              Siguiente <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-title>
      </v-card>

      <!-- Lienzo + Panel Lateral -->
      <v-row dense>
        <!-- SECCIÓN DEL LIENZO / CANVAS -->
        <v-col cols="12" md="8">
          <v-card class="rounded-xl border-light mb-4" outlined elevation="0">
            <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-crop-free</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                  Lienzo de Etiquetado
                </span>
              </div>

              <div class="d-flex align-center">
                <v-btn 
                  small 
                  outlined 
                  color="primary" 
                  class="font-weight-bold mr-2" 
                  :loading="isAnalyzing"
                  @click="reAnalyzeCurrent"
                >
                  <v-icon left small>mdi-auto-fix</v-icon>
                  Auto-Detectar IA
                </v-btn>
                <v-btn 
                  small 
                  text 
                  color="error" 
                  class="font-weight-bold" 
                  @click="clearCurrentBoxes"
                >
                  <v-icon left small>mdi-delete-sweep-outline</v-icon>
                  Limpiar Cajas
                </v-btn>
              </div>
            </v-card-title>

            <v-card-text class="pt-4 text-center">
              <div class="canvas-container">
                <canvas 
                  ref="canvas" 
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  style="cursor: crosshair; max-width: 100%; display: block;"
                ></canvas>
                
                <!-- Overlay de análisis de IA -->
                <v-overlay absolute :value="isAnalyzing" color="white" opacity="0.85">
                  <div class="text-center primary--text">
                    <v-progress-circular indeterminate size="54" width="5" color="primary"></v-progress-circular>
                    <div class="mt-4 text-subtitle-1 font-weight-bold grey--text text--darken-4">
                      La IA está detectando los mangos...
                    </div>
                  </div>
                </v-overlay>
              </div>

              <div class="caption grey--text text--darken-1 mt-3">
                Haz clic y arrastra sobre la foto para delimitar mangos no detectados manualmente.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- SECCIÓN DE ADMINISTRACIÓN DE ETIQUETAS -->
        <v-col cols="12" md="4">
          <v-card class="rounded-xl border-light mb-4" outlined elevation="0">
            <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-tag-multiple-outline</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                  Mangos Detectados
                </span>
              </div>
              <v-chip color="primary" small outlined class="font-weight-bold">
                {{ boxes.length }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-4 px-4">
              <!-- Asignación Rápida para todos los mangos de esta foto -->
              <div v-if="boxes.length > 0" class="mb-3 pa-2 rounded-lg bg-grey-lighten-5 border-light">
                <div class="caption font-weight-bold grey--text text--darken-3 mb-1">
                  Asignar a todos los mangos de esta foto:
                </div>
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="lvl in maturityLevels"
                    :key="'quick-' + lvl.value"
                    x-small
                    outlined
                    color="primary"
                    class="mr-1 mb-1 font-weight-bold"
                    @click="applyMaturityToCurrentPhoto(lvl.value)"
                  >
                    Nivel {{ lvl.value }}
                  </v-chip>
                </div>
              </div>

              <!-- Lista de Mangos -->
              <v-list v-if="boxes.length > 0" class="pa-0 list-scroll" style="max-height: 380px; overflow-y: auto;">
                <v-card 
                  v-for="(box, index) in boxes" 
                  :key="index" 
                  class="mb-2 pa-2 rounded-lg border-light" 
                  outlined 
                  elevation="0"
                >
                  <div class="d-flex align-center justify-space-between mb-1">
                    <span class="caption font-weight-bold grey--text text--darken-4">
                      Mango #{{ index + 1 }}
                    </span>
                    <div class="d-flex align-center">
                      <v-chip 
                        x-small 
                        :color="box.label !== null ? 'success' : 'error'" 
                        outlined 
                        class="font-weight-bold mr-1"
                      >
                        {{ box.label !== null ? `M: ${parseFloat(box.label).toFixed(1)}` : 'Sin asignar' }}
                      </v-chip>
                      <v-btn icon x-small color="error" @click="deleteBox(index)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <v-chip-group
                    v-model="box.label"
                    active-class="primary--text font-weight-bold"
                    column
                    @change="onBoxLabelChange"
                  >
                    <v-chip
                      v-for="level in maturityLevels"
                      :key="level.value"
                      :value="level.value"
                      :color="box.label === level.value ? level.color : ''"
                      :dark="box.label === level.value"
                      x-small
                      class="mr-1 mb-1 font-weight-medium"
                    >
                      {{ level.value }}
                    </v-chip>
                  </v-chip-group>
                </v-card>
              </v-list>
              
              <div v-else class="text-center py-6 grey--text text--darken-1 caption">
                <v-icon size="36" color="grey lighten-1">mdi-gesture-swipe</v-icon>
                <div class="mt-2">No se han delimitado mangos aún.</div>
                <div>Haz clic y arrastra sobre la foto o pulsa 'Auto-Detectar IA'.</div>
              </div>

              <v-divider class="my-3"></v-divider>
              
              <!-- Botones de Guardar -->
              <v-btn 
                color="primary" 
                block 
                large
                depressed
                class="font-weight-bold mb-2"
                :disabled="boxes.length === 0"
                @click="saveCurrentAndReturn"
              >
                <v-icon left>mdi-content-save-check-outline</v-icon>
                Guardar y Volver a Galería
              </v-btn>

              <v-btn 
                v-if="activeIndex < batchImages.length - 1"
                color="primary" 
                block 
                outlined
                class="font-weight-bold"
                :disabled="boxes.length === 0"
                @click="saveCurrentAndNext"
              >
                <v-icon left small>mdi-arrow-right-circle-outline</v-icon>
                Guardar y Siguiente Foto
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Diálogo de confirmación de Reentrenamiento (Estilo Neutro Estándar) -->
    <v-dialog v-model="showRetrainDialog" max-width="520" persistent>
      <v-card class="rounded-xl border-light">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-rotate-3d-variant</v-icon>
            <span class="text-subtitle-1 font-weight-black grey--text text--darken-4">
              Confirmar Reentrenamiento
            </span>
          </div>
          <v-btn icon small color="grey darken-2" @click="showRetrainDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="py-4 px-5 grey--text text--darken-3">
          <p class="text-body-2 mb-3">
            ¿Deseas iniciar un nuevo reentrenamiento del modelo de IA con las imágenes etiquetadas del dataset?
          </p>
          <div class="pa-3 rounded-lg bg-grey-lighten-5 border-light text-caption">
            <div class="d-flex align-center mb-1">
              <v-icon x-small color="grey darken-2" class="mr-2">mdi-clock-outline</v-icon>
              <span>El proceso se ejecuta en segundo plano en el servidor.</span>
            </div>
            <div class="d-flex align-center mb-1">
              <v-icon x-small color="grey darken-2" class="mr-2">mdi-database-outline</v-icon>
              <span>Se procesarán todas las muestras guardadas en el dataset.</span>
            </div>
            <div class="d-flex align-center">
              <v-icon x-small color="grey darken-2" class="mr-2">mdi-check-decagram-outline</v-icon>
              <span>Al culminar con éxito, el modelo activo se actualizará automáticamente.</span>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-3 px-5">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" class="font-weight-bold" @click="showRetrainDialog = false">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            depressed 
            class="font-weight-bold" 
            @click="startRetraining" 
            :loading="trainingStatus === 'TRAINING'"
          >
            Confirmar e Iniciar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _sMangoTraining from "@/services/Quality/MangoTrainingService";

export default {
  name: "MangoTraining",
  data: () => ({
    // Niveles de Madurez estándar
    maturityLevels: [
      { value: 1, color: "green darken-2" },
      { value: 1.5, color: "light-green darken-1" },
      { value: 2, color: "lime darken-2" },
      { value: 2.5, color: "yellow darken-1" },
      { value: 3, color: "amber darken-2" },
      { value: 4, color: "orange darken-2" },
      { value: 5, color: "deep-orange darken-2" }
    ],

    // Modos de Vista: 'gallery' | 'canvas'
    viewMode: "gallery",

    // Lote de imágenes
    batchImages: [], // Array de { id, file, url, boxes, saved, assignedMaturity, detected }
    maxBatchLimit: 30,
    currentPage: 1,
    pageSize: 8,
    globalBatchMaturity: 2,

    // Progreso de procesamiento masivo
    isBatchProcessing: false,
    batchProgressIndex: 0,
    batchProgressText: "",

    // Estado del Lienzo activo
    activeIndex: 0,
    imageObj: null,
    isDrawing: false,
    isAnalyzing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    scaleFactor: 1,
    boxes: [], // Cajas de la imagen activa en lienzo

    // Reentrenamiento y estado del modelo
    trainingStatus: "IDLE", // IDLE, TRAINING, COMPLETED, ERROR
    trainingMessage: "Listo para entrenar",
    showRetrainDialog: false,
    modelVersion: null,
    statusPollingInterval: null
  }),

  computed: {
    hasRetrainPermission() {
      return this.$fun.hasSpecialPermission("retrain") || this.$fun.hasSpecialPermission("reentrenar");
    },
    savedCount() {
      return this.batchImages.filter(item => item.saved).length;
    },
    pendingCount() {
      return this.batchImages.filter(item => !item.saved).length;
    },
    totalPages() {
      return Math.ceil(this.batchImages.length / this.pageSize) || 1;
    },
    paginatedBatch() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.batchImages.slice(start, start + this.pageSize);
    },
    currentItem() {
      return this.batchImages[this.activeIndex] || null;
    },
    batchProgressPercent() {
      if (this.batchImages.length === 0) return 0;
      return Math.round((this.batchProgressIndex / this.batchImages.length) * 100);
    }
  },

  mounted() {
    this.fetchModelVersion();
  },

  beforeDestroy() {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
    // Liberar URLs de blob
    this.revokeAllBlobUrls();
  },

  methods: {
    // -------------------------------------------------------------------------
    // 1. CARGA Y GESTIÓN DEL LOTE DE IMÁGENES
    // -------------------------------------------------------------------------
    onFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      let selectedFiles = files;
      if (files.length > this.maxBatchLimit) {
        this.$fun.alert(
          `Se seleccionaron ${files.length} imágenes. Se tomaron las primeras ${this.maxBatchLimit} para garantizar la estabilidad del sistema.`,
          "info"
        );
        selectedFiles = files.slice(0, this.maxBatchLimit);
      }

      this.revokeAllBlobUrls();

      this.batchImages = selectedFiles.map((file, idx) => ({
        id: `img_${Date.now()}_${idx}`,
        file,
        url: URL.createObjectURL(file),
        originalIndex: idx,
        boxes: [],
        saved: false,
        assignedMaturity: null,
        detected: false
      }));

      this.currentPage = 1;
      this.viewMode = "gallery";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    clearBatch() {
      this.revokeAllBlobUrls();
      this.batchImages = [];
      this.currentPage = 1;
      this.viewMode = "gallery";
      this.boxes = [];
      this.imageObj = null;
    },

    revokeAllBlobUrls() {
      this.batchImages.forEach(item => {
        if (item.url && item.url.startsWith("blob:")) {
          URL.revokeObjectURL(item.url);
        }
      });
    },

    applyGlobalMaturityToAll() {
      this.batchImages.forEach(img => {
        img.assignedMaturity = this.globalBatchMaturity;
        if (img.boxes && img.boxes.length > 0) {
          img.boxes.forEach(b => {
            b.label = this.globalBatchMaturity;
          });
        }
      });
      this.$fun.alert(
        `Nivel de madurez ${this.globalBatchMaturity} asignado a todas las imágenes del lote.`,
        "success"
      );
    },

    // -------------------------------------------------------------------------
    // 2. PROCESAMIENTO MASIVO SECUENCIAL (LOTE HOMOGÉNEO)
    // -------------------------------------------------------------------------
    async processEntireBatch() {
      const pendingItems = this.batchImages.filter(item => !item.saved);
      if (pendingItems.length === 0) {
        this.$fun.alert("Todas las imágenes de este lote ya han sido guardadas.", "info");
        return;
      }

      this.isBatchProcessing = true;
      this.batchProgressIndex = 0;
      let processedTotal = 0;

      for (let i = 0; i < this.batchImages.length; i++) {
        const item = this.batchImages[i];
        if (item.saved) {
          this.batchProgressIndex = i + 1;
          continue;
        }

        this.batchProgressText = `Procesando imagen ${i + 1} de ${this.batchImages.length} (${item.file.name})...`;

        try {
          // 1. Detección automática con IA si no tiene cajas
          let itemBoxes = item.boxes;
          if (!item.detected || itemBoxes.length === 0) {
            const formDataDetect = new FormData();
            formDataDetect.append("image", item.file);
            const detectRes = await _sMangoTraining.detect(formDataDetect, this.$fun.getUserID());
            const data = typeof detectRes.data === "string" ? JSON.parse(detectRes.data) : detectRes.data;

            if (data.detections && data.detections.length > 0) {
              itemBoxes = data.detections.map(det => ({
                x: det.x,
                y: det.y,
                width: det.width,
                height: det.height,
                label: item.assignedMaturity || this.globalBatchMaturity
              }));
              item.boxes = itemBoxes;
              item.detected = true;
            }
          }

          if (itemBoxes.length === 0) {
            // No se detectaron mangos, saltar
            this.batchProgressIndex = i + 1;
            continue;
          }

          // Asignar madurez por defecto a todas las cajas
          itemBoxes.forEach(b => {
            if (b.label === null || b.label === undefined) {
              b.label = item.assignedMaturity || this.globalBatchMaturity;
            }
          });

          // 2. Convertir a formato YOLO normalizado
          const dimensions = await this.getImageDimensions(item.file);
          const yoloData = this.generateYoloDataFromBoxes(itemBoxes, dimensions.width, dimensions.height);

          // 3. Guardar en el servidor
          const formDataSave = new FormData();
          formDataSave.append("image", item.file);
          formDataSave.append("labels", yoloData);

          const saveRes = await _sMangoTraining.save(formDataSave, this.$fun.getUserID());
          if (saveRes.data) {
            item.saved = true;
            processedTotal++;
          }
        } catch (err) {
          console.error(`Error procesando imagen ${item.file.name}:`, err);
        }

        this.batchProgressIndex = i + 1;
      }

      this.isBatchProcessing = false;
      this.$fun.alert(
        `Procesamiento completado: se guardaron exitosamente ${processedTotal} imágenes en el dataset.`,
        "success"
      );
    },

    getImageDimensions(file) {
      return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(file);
      });
    },

    generateYoloDataFromBoxes(boxes, imgWidth, imgHeight) {
      const classesMapping = { "1": 0, "1.5": 1, "2": 2, "2.5": 3, "3": 4, "4": 5, "5": 6 };
      return boxes.map(box => {
        const classIndex = classesMapping[box.label.toString()] ?? 0;
        const x_center = (box.x + (box.width / 2)) / imgWidth;
        const y_center = (box.y + (box.height / 2)) / imgHeight;
        const w_norm = box.width / imgWidth;
        const h_norm = box.height / imgHeight;
        return `${classIndex} ${x_center.toFixed(6)} ${y_center.toFixed(6)} ${w_norm.toFixed(6)} ${h_norm.toFixed(6)}`;
      }).join("\n");
    },

    // -------------------------------------------------------------------------
    // 3. NAVEGACIÓN Y OPERACIONES DEL LIENZO
    // -------------------------------------------------------------------------
    openCanvas(index) {
      this.activeIndex = index;
      this.viewMode = "canvas";
      this.$nextTick(() => {
        this.loadActiveImageToCanvas();
      });
    },

    backToGallery() {
      // Guardar el estado actual de cajas en el item del lote
      if (this.currentItem) {
        this.currentItem.boxes = [...this.boxes];
        this.syncCurrentItemMaturity();
      }
      this.viewMode = "gallery";
    },

    navigateImage(step) {
      if (this.currentItem) {
        this.currentItem.boxes = [...this.boxes];
        this.syncCurrentItemMaturity();
      }
      const newIdx = this.activeIndex + step;
      if (newIdx >= 0 && newIdx < this.batchImages.length) {
        this.activeIndex = newIdx;
        this.$nextTick(() => {
          this.loadActiveImageToCanvas();
        });
      }
    },

    loadActiveImageToCanvas() {
      if (!this.currentItem) return;

      this.imageObj = new Image();
      this.imageObj.onload = () => {
        const canvas = this.$refs.canvas;
        if (!canvas) return;

        const MAX_WIDTH = 800;
        let scale = 1;
        if (this.imageObj.width > MAX_WIDTH) {
          scale = MAX_WIDTH / this.imageObj.width;
        }

        canvas.width = this.imageObj.width * scale;
        canvas.height = this.imageObj.height * scale;
        this.scaleFactor = scale;

        // Si la imagen ya tenía cajas en el lote, escalarlas al canvas
        if (this.currentItem.boxes && this.currentItem.boxes.length > 0) {
          this.boxes = this.currentItem.boxes.map(b => ({
            ...b,
            x: b.isScaled ? b.x : b.x * scale,
            y: b.isScaled ? b.y : b.y * scale,
            width: b.isScaled ? b.width : b.width * scale,
            height: b.isScaled ? b.height : b.height * scale,
            isScaled: true
          }));
          this.redrawCanvas();
        } else {
          this.boxes = [];
          this.redrawCanvas();
        }
      };
      this.imageObj.src = this.currentItem.url;
    },

    async analyzeCurrentImage() {
      if (!this.currentItem) return;

      this.isAnalyzing = true;
      try {
        const formData = new FormData();
        formData.append("image", this.currentItem.file);

        const response = await _sMangoTraining.detect(formData, this.$fun.getUserID());
        const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

        if (data.detections && data.detections.length > 0) {
          this.boxes = data.detections.map(det => ({
            x: det.x * this.scaleFactor,
            y: det.y * this.scaleFactor,
            width: det.width * this.scaleFactor,
            height: det.height * this.scaleFactor,
            label: this.currentItem.assignedMaturity || null,
            isScaled: true
          }));
          this.currentItem.boxes = [...this.boxes];
          this.currentItem.detected = true;
          this.redrawCanvas();
          this.syncCurrentItemMaturity();
          this.$fun.alert(
            `¡La IA detectó ${this.boxes.length} mangos! Revisa y asigna la madurez.`,
            "success"
          );
        } else {
          this.currentItem.detected = true;
          this.$fun.alert("La IA no detectó mangos con suficiente confianza. Puedes trazarlos manualmente.", "info");
        }
      } catch (err) {
        console.error(err);
        this.$fun.alert("Error al conectar con el microservicio de IA.", "error");
      } finally {
        this.isAnalyzing = false;
      }
    },

    reAnalyzeCurrent() {
      this.boxes = [];
      this.analyzeCurrentImage();
    },

    clearCurrentBoxes() {
      this.boxes = [];
      if (this.currentItem) {
        this.currentItem.boxes = [];
        this.currentItem.assignedMaturity = null;
      }
      this.redrawCanvas();
    },

    applyMaturityToCurrentPhoto(maturityLevel) {
      this.boxes.forEach(b => {
        b.label = maturityLevel;
      });
      if (this.currentItem) {
        this.currentItem.assignedMaturity = maturityLevel;
        this.currentItem.boxes = [...this.boxes];
      }
      this.redrawCanvas();
    },

    // -------------------------------------------------------------------------
    // 4. DIBUJO EN CANVAS
    // -------------------------------------------------------------------------
    getMousePos(evt) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (evt.clientX - rect.left) * scaleX,
        y: (evt.clientY - rect.top) * scaleY
      };
    },

    startDrawing(e) {
      if (!this.imageObj) return;
      this.isDrawing = true;
      const pos = this.getMousePos(e);
      this.startX = pos.x;
      this.startY = pos.y;
      this.currentX = pos.x;
      this.currentY = pos.y;
    },

    draw(e) {
      if (!this.isDrawing) return;
      const pos = this.getMousePos(e);
      this.currentX = pos.x;
      this.currentY = pos.y;
      this.redrawCanvas();

      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#1976D2";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        this.startX,
        this.startY,
        this.currentX - this.startX,
        this.currentY - this.startY
      );
      ctx.setLineDash([]);
    },

    stopDrawing() {
      if (!this.isDrawing) return;
      this.isDrawing = false;

      const width = this.currentX - this.startX;
      const height = this.currentY - this.startY;

      if (Math.abs(width) > 20 && Math.abs(height) > 20) {
        this.boxes.push({
          x: width > 0 ? this.startX : this.currentX,
          y: height > 0 ? this.startY : this.currentY,
          width: Math.abs(width),
          height: Math.abs(height),
          label: null,
          isScaled: true
        });
      }
      this.redrawCanvas();
      this.syncCurrentItemMaturity();
    },

    redrawCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.imageObj) return;
      const ctx = canvas.getContext("2d");

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(this.imageObj, 0, 0, canvas.width, canvas.height);

      this.boxes.forEach((box, index) => {
        ctx.strokeStyle = box.label !== null ? "#2E7D32" : "#D32F2F";
        ctx.lineWidth = 3;
        ctx.strokeRect(box.x, box.y, box.width, box.height);

        ctx.fillStyle = ctx.strokeStyle;
        const tagHeight = 22;
        const tagY = Math.max(0, box.y - tagHeight);
        ctx.fillRect(box.x, tagY, 115, tagHeight);

        ctx.fillStyle = "white";
        ctx.font = "bold 12px Arial";
        const labelText = box.label !== null ? `M: ${parseFloat(box.label).toFixed(1)}` : "Sin clasificar";
        ctx.fillText(`#${index + 1} ${labelText}`, box.x + 5, tagY + 15);
      });
    },

    deleteBox(index) {
      this.boxes.splice(index, 1);
      this.redrawCanvas();
      this.syncCurrentItemMaturity();
    },

    onBoxLabelChange() {
      this.redrawCanvas();
      this.syncCurrentItemMaturity();
    },

    syncCurrentItemMaturity() {
      if (!this.currentItem) return;
      this.currentItem.boxes = [...this.boxes];
      const labeled = this.boxes.filter(b => b.label !== null && b.label !== undefined);
      if (labeled.length === 0) {
        this.currentItem.assignedMaturity = null;
      } else {
        const unique = [...new Set(labeled.map(b => b.label.toString()))];
        if (unique.length === 1) {
          this.currentItem.assignedMaturity = parseFloat(unique[0]);
        } else {
          this.currentItem.assignedMaturity = "Mixto";
        }
      }
    },

    getMaturityBadge(imgItem) {
      if (!imgItem) return null;
      const boxes = imgItem.boxes || [];
      const labeledBoxes = boxes.filter(b => b.label !== null && b.label !== undefined);

      if (labeledBoxes.length > 0) {
        const uniqueLabels = [...new Set(labeledBoxes.map(b => b.label.toString()))];
        if (uniqueLabels.length === 1) {
          const val = parseFloat(uniqueLabels[0]);
          const lvl = this.maturityLevels.find(l => l.value === val);
          return {
            text: `Madurez ${val.toFixed(1)}`,
            color: lvl ? lvl.color : "primary",
            dark: true
          };
        } else {
          return {
            text: "Madurez Mixta",
            color: "primary",
            dark: false
          };
        }
      }

      if (imgItem.assignedMaturity !== null && imgItem.assignedMaturity !== undefined) {
        if (imgItem.assignedMaturity === "Mixto") {
          return {
            text: "Madurez Mixta",
            color: "primary",
            dark: false
          };
        }
        const val = parseFloat(imgItem.assignedMaturity);
        const lvl = this.maturityLevels.find(l => l.value === val);
        return {
          text: `Madurez ${val.toFixed(1)}`,
          color: lvl ? lvl.color : "primary",
          dark: true
        };
      }

      return null;
    },

    // -------------------------------------------------------------------------
    // 5. GUARDADO DE LA IMAGEN ACTIVA
    // -------------------------------------------------------------------------
    async saveCurrentAndReturn() {
      const success = await this.saveActiveImage();
      if (success) {
        this.backToGallery();
      }
    },

    async saveCurrentAndNext() {
      const success = await this.saveActiveImage();
      if (success && this.activeIndex < this.batchImages.length - 1) {
        this.navigateImage(1);
      }
    },

    async saveActiveImage() {
      const unlabelled = this.boxes.find(b => b.label === null || b.label === undefined);
      if (unlabelled) {
        this.$fun.alert("Hay mangos sin clasificar. Asigna el nivel de madurez a todos antes de guardar.", "warning");
        return false;
      }

      if (this.boxes.length === 0) {
        this.$fun.alert("Debes delimitar al menos un mango antes de guardar.", "warning");
        return false;
      }

      const canvas = this.$refs.canvas;
      const classesMapping = { "1": 0, "1.5": 1, "2": 2, "2.5": 3, "3": 4, "4": 5, "5": 6 };
      const yoloData = this.boxes.map(box => {
        const classIndex = classesMapping[box.label.toString()] ?? 0;
        const x_center = (box.x + (box.width / 2)) / canvas.width;
        const y_center = (box.y + (box.height / 2)) / canvas.height;
        const w_norm = box.width / canvas.width;
        const h_norm = box.height / canvas.height;
        return `${classIndex} ${x_center.toFixed(6)} ${y_center.toFixed(6)} ${w_norm.toFixed(6)} ${h_norm.toFixed(6)}`;
      }).join("\n");

      try {
        const formData = new FormData();
        formData.append("image", this.currentItem.file);
        formData.append("labels", yoloData);

        const response = await _sMangoTraining.save(formData, this.$fun.getUserID());
        if (response.data) {
          this.currentItem.saved = true;
          this.currentItem.boxes = [...this.boxes];
          this.syncCurrentItemMaturity();
          this.$fun.alert(`¡Foto guardada exitosamente en el dataset!`, "success");
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        const serverError = err.response?.data?.Message || err.response?.data || err.message;
        this.$fun.alert(`Error al guardar: ${serverError}`, "error");
        return false;
      }
    },

    // -------------------------------------------------------------------------
    // 6. REENTRENAMIENTO Y ESTADO DEL MODELO IA
    // -------------------------------------------------------------------------
    getTrainingStatusColor() {
      switch (this.trainingStatus) {
        case "TRAINING": return "warning";
        case "COMPLETED": return "success";
        case "ERROR": return "error";
        default: return "grey";
      }
    },

    getTrainingStatusText() {
      switch (this.trainingStatus) {
        case "TRAINING": return "Entrenando...";
        case "COMPLETED": return "Modelo actualizado";
        case "ERROR": return "Error en entrenamiento";
        default: return "Modelo al día";
      }
    },

    async fetchModelVersion() {
      try {
        const response = await _sMangoTraining.getModelVersion();
        const data = response.data;
        this.modelVersion = data.version || data.updated_at || "1.0.0";
      } catch (e) {
        console.warn("No se pudo obtener la versión del modelo:", e);
      }
    },

    async fetchTrainingStatus() {
      try {
        const response = await _sMangoTraining.getTrainingStatus(this.$fun.getUserID());
        const data = response.data;
        this.trainingStatus = data.status || "IDLE";
        this.trainingMessage = data.message || "";

        if (this.trainingStatus === "COMPLETED" || this.trainingStatus === "ERROR") {
          this.stopStatusPolling();
          this.fetchModelVersion();
          if (this.trainingStatus === "COMPLETED") {
            this.$fun.alert("¡Reentrenamiento completado con éxito! El modelo ha sido actualizado.", "success");
          } else {
            this.$fun.alert(`Error en reentrenamiento: ${this.trainingMessage}`, "error");
          }
        }
      } catch (e) {
        console.error("Error consultando estado de entrenamiento:", e);
        this.stopStatusPolling();
      }
    },

    startStatusPolling() {
      this.fetchTrainingStatus();
      this.statusPollingInterval = setInterval(() => this.fetchTrainingStatus(), 5000);
    },

    stopStatusPolling() {
      if (this.statusPollingInterval) {
        clearInterval(this.statusPollingInterval);
        this.statusPollingInterval = null;
      }
    },

    confirmRetrain() {
      if (!this.hasRetrainPermission) {
        this.$fun.alert("No cuenta con permisos especiales para reentrenar el modelo.", "warning");
        return;
      }
      this.showRetrainDialog = true;
    },

    async startRetraining() {
      if (!this.hasRetrainPermission) {
        this.$fun.alert("No cuenta con permisos especiales para reentrenar el modelo.", "warning");
        return;
      }
      this.showRetrainDialog = false;
      try {
        const response = await _sMangoTraining.startTraining(this.$fun.getUserID());
        const data = response.data;

        if (data.status === "STARTED") {
          this.$fun.alert("Entrenamiento iniciado en segundo plano. Se notificará al finalizar.", "success");
          this.startStatusPolling();
        } else if (data.status === "ALREADY_RUNNING") {
          this.$fun.alert("Ya hay un entrenamiento en curso.", "warning");
          this.startStatusPolling();
        } else {
          this.$fun.alert(data.message || "Estado desconocido", "info");
        }
      } catch (error) {
        console.error(error);
        const message = error.response?.data?.Message || error.response?.data || error.message || "Error al iniciar reentrenamiento";
        this.$fun.alert(message, "error");
      }
    }
  }
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.bg-grey-lighten-5 {
  background-color: #f8fafc !important;
}
.collage-card {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}
.collage-card:hover {
  border-color: #1976D2 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}
.image-preview-wrapper {
  position: relative;
  border-bottom: 1px solid #e2e8f0;
}
.status-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
}
.canvas-container {
  display: inline-block;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background-color: #fafafa;
}
.list-scroll::-webkit-scrollbar {
  width: 5px;
}
.list-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
</style>
